import { ReactElement } from 'react'
import { useWatch } from 'react-hook-form'
import { Alert, Stack, Typography } from '@mui/material'
import { Select, TextField } from '@ui/forms'
import type { ProjectFieldFormValues } from '../schema'
import {
  MAX_TITLE_LENGTH,
  TITLE_PATTERN,
  fieldTypeLabels,
  normalizeTitle,
  templatePlaceholder,
} from '../utils'
import ProjectFieldOptionList from './ProjectFieldOptionList'

interface Props {
  isEdit: boolean
}

const typeOptions = (['text', 'select'] as const).map(value => ({
  value,
  label: fieldTypeLabels[value],
}))

const ProjectFieldFormFields = ({ isEdit }: Props): ReactElement => {
  const fieldType = useWatch<ProjectFieldFormValues, 'field_type'>({ name: 'field_type' })
  const title = useWatch<ProjectFieldFormValues, 'title'>({ name: 'title' })
  const hasValidTitle = TITLE_PATTERN.test(normalizeTitle(title ?? ''))

  return (
    <Stack spacing={2}>
      <Select<ProjectFieldFormValues>
        name="field_type"
        label="Typ"
        options={typeOptions}
        // Projects store text for one type and an option for the other.
        disabled={isEdit}
        tooltip={
          isEdit
            ? { text: 'Der Typ eines bestehenden Felds kann nicht geändert werden.' }
            : undefined
        }
        data-cy="project-field-type"
      />
      <Stack spacing={0.5}>
        <TextField
          name="title"
          label="Titel"
          placeholder={`z. B. Architekt (max. ${MAX_TITLE_LENGTH} Zeichen, ohne Umlaute)`}
          data-cy="project-field-title"
        />
        <Typography variant="caption" color="text.secondary" data-cy="project-field-placeholder">
          Platzhalter in Deckblatt und Kopf-/Fußzeile:{' '}
          <code>{hasValidTitle ? templatePlaceholder(title) : '–'}</code>
        </Typography>
      </Stack>
      {isEdit && (
        <Alert severity="info" sx={{ py: 0 }}>
          Wird der Titel geändert, ändert sich auch der Platzhalter. Eigene Vorlagen, die den alten
          Platzhalter verwenden, müssen angepasst werden.
        </Alert>
      )}
      <TextField
        name="description"
        label="Beschreibung"
        multiline
        rows={2}
        placeholder="Wird im Projektformular als Hilfetext angezeigt"
        data-cy="project-field-description"
      />
      {fieldType === 'select' && <ProjectFieldOptionList />}
    </Stack>
  )
}

export default ProjectFieldFormFields
